import { type ActorView, getProfile, type PostView, type ProfileView } from "../queries.ts";
import type { RpcClient } from "./api.ts";

// A click opens something the page already described: a thread's focal post, a profile's
// identity, the DID behind a handle. Remembering what was rendered lets those pages draw it
// before their own requests return. Any entry only stands in until fresh data arrives, so the
// newest view wins and old ones are simply forgotten.
const limit = 1000;
const posts = new Map<string, PostView>();
const actors = new Map<string, ActorView>();
const profiles = new Map<string, ProfileView>();

function put<T>(map: Map<string, T>, keys: readonly string[], value: T): void {
  for (const key of keys) {
    map.delete(key);
    map.set(key, value);
  }
  while (map.size > limit) map.delete(map.keys().next().value!);
}

/** Actors are found by DID or by handle, as routes name them either way. */
function keysOf(actor: ActorView): string[] {
  return [actor.did, ...(actor.handle ? [actor.handle.toLowerCase()] : [])];
}

function key(actor: string): string {
  return actor.startsWith("did:") ? actor : actor.toLowerCase();
}

export function cachePost(post: PostView): void {
  put(posts, [post.uri], post);
  cacheActor(post.author);
  if (post.replyTo) cacheActor(post.replyTo);
}

export function cacheActor(actor: ActorView): void {
  put(actors, keysOf(actor), actor);
}

export function cacheProfile(profile: ProfileView): void {
  put(profiles, keysOf(profile.actor), profile);
  cacheActor(profile.actor);
}

export function cachedPost(uri: string): PostView | undefined {
  return posts.get(uri);
}

export function cachedActor(actor: string): ActorView | undefined {
  return actors.get(key(actor));
}

export function cachedProfile(actor: string): ProfileView | undefined {
  return profiles.get(key(actor));
}

/** Handle-addressed routes need a DID before they can fetch anything. */
export async function resolveDid(
  client: RpcClient,
  actor: string,
  signal: AbortSignal,
): Promise<`did:${string}`> {
  if (actor.startsWith("did:")) return actor as `did:${string}`;
  const known = cachedActor(actor);
  if (known) return known.did;
  const profile = await client.call(getProfile, { parameters: { actor }, signal });
  cacheProfile(profile);
  return profile.actor.did;
}
